import { Box } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import { Button, Input, Typography } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import i18n from 'i18next'
import useEnterKey from 'src/utils/EnterKeyHandler'
import { useRouter } from 'next/router'
import k from './../../i18n/keys'
import { useBackend } from '@/src/providers/BackendProvider'

const PASSWORDS_DO_NOT_MATCH = i18n.t(k.THE_PASSWORDS_DO_NOT_MATCH_PL)

const SetPassword = ({ setCurrentView, type }) => {
  const { asyncUserValidate, asyncUserLogIn, decryptUser, userUpdatePassword } =
    useBackend()
  const router = useRouter()

  const [pwdStatus, setPwdStatus] = useState({
    password: i18n.t(k._),
    confirmPassword: i18n.t(k._),
    showPassword: false,
    showConfirm: false
  })

  const [errors, setErrors] = useState({
    password: i18n.t(k._),
    confirmPassword: i18n.t(k._)
  })

  const [username, setUsername] = useState()
  const continueButtonRef = useRef(null)

  useEffect(() => {
    verifyCode()
  }, [])

  const handleExpiredCode = () => {
    setTimeout(() => {
      window.location.href = '/SignIn'
    }, 10000)
    window.alert(
      'Sorry, this link is invalid or expired. Make sure you use the correct link from your email! This page will automatically redirect to LogIn page.'
    )
  }

  const verifyCode = async () => {
    const queryString = new URLSearchParams(window.location.search)
    if (queryString.get('Code')) {
      try {
        const decryptionData = await decryptUser(queryString.get('Code'))
        if (decryptionData.is_expired) {
          handleExpiredCode()
          return
        }
        setUsername(decryptionData.email)
      } catch (e) {
        handleExpiredCode()
      }
    }
  }

  const checkInfo = () => {
    return (
      errors.password === '' &&
      errors.confirmPassword === '' &&
      pwdStatus.password.length >= 6 &&
      pwdStatus.confirmPassword.length >= 6 &&
      pwdStatus.password === pwdStatus.confirmPassword
    )
  }

  const handleClickShowPassword = (prop) => () => {
    if (prop === 'password') {
      setPwdStatus({ ...pwdStatus, showPassword: !pwdStatus.showPassword })
    } else {
      setPwdStatus({ ...pwdStatus, showConfirm: !pwdStatus.showConfirm })
    }
  }

  const handleChange = (prop) => (event) => {
    setPwdStatus({ ...pwdStatus, [prop]: event.target.value })
    if (prop === 'password') {
      setErrors((latestErrors) => {
        return { ...latestErrors, password: i18n.t(k._) }
      })
    }
  }
  // every time the pwd status changes, update error message (or it will be delayed)
  useEffect(() => {
    if (pwdStatus.confirmPassword && pwdStatus.password) {
      if (pwdStatus.confirmPassword !== pwdStatus.password) {
        setErrors({ ...errors, confirmPassword: PASSWORDS_DO_NOT_MATCH })
      } else {
        setErrors({ ...errors, confirmPassword: i18n.t(k._) })
      }
    }
  }, [pwdStatus])

  const handleClick = async () => {
    if (checkInfo()) {
      let username_sent
      let code = new URLSearchParams(window.location.search).get('Code')
      if (!username) {
        const decryptionData = await decryptUser(code)
        username_sent = decryptionData.email
      } else {
        username_sent = username
      }
      try {
        const mode = new URLSearchParams(window.location.search).get('mode')
        if (mode === 'verifyEmail') {
          const validateRes = await asyncUserValidate({
            username: username_sent,
            code
          })
          code = validateRes.data.temp_code
        }
        await userUpdatePassword({
          code,
          username: username_sent,
          password: pwdStatus.password
        })
      } catch (error) {
        setErrors((latestErrors) => ({
          ...latestErrors,
          general: 'Failed to reset password, please try again later.'
        }))
        return
      }

      if (type === 'set') {
        await asyncUserLogIn(username_sent, pwdStatus.password)
        setCurrentView('Profile')
      } else if (type === 'reset') {
        router.push('/SignIn')
      }
      if (pwdStatus.password.length < 6) {
        setErrors((latestErrors) => {
          return {
            ...latestErrors,
            password: i18n.t(k.PASSWORD_MUST_BE_CHARACTERS)
          }
        })
      }
      if (pwdStatus.confirmPassword.length < 6) {
        setErrors((latestErrors) => {
          return {
            ...latestErrors,
            confirmPassword: i18n.t(k.CONFIRM_PASSWORD_MUST_BE_CH)
          }
        })
      }
      if (pwdStatus.confirmPassword !== pwdStatus.password) {
        setErrors((latestErrors) => {
          return { ...latestErrors, confirmPassword: PASSWORDS_DO_NOT_MATCH }
        })
      }
    } else {
      throw Error('Unknown error')
    }
  }

  const handleBackToSignIn = () => {
    router.push('/SignIn')
  }

  useEnterKey(continueButtonRef)

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Typography variant="h4">
        {i18n.t(k.PLEASE)}{' '}
        {type === i18n.t(k.SET) ? i18n.t(k.SET) : i18n.t(k.RESET)}{' '}
        {i18n.t(k.YOUR_PASSWORD)}
      </Typography>
      <div>
        <Input
          placeholder="Password"
          controlSx={{
            marginTop: i18n.t(k.PX21),
            width: '100%',
            display: i18n.t(k.FLEX)
          }}
          value={pwdStatus.password}
          required
          type="password"
          fullWidth
          onChange={handleChange('password')}
          errormsg={errors.password}
          onBlur={() => {
            if (pwdStatus.password.length < 6) {
              setErrors((latestErrors) => {
                return {
                  ...latestErrors,
                  password: i18n.t(k.PASSWORD_MUST_BE_CHARACTERS)
                }
              })
            }
          }}
          id="sign-in-page-set-password-view-password"
        />

        <Input
          placeholder="Confirm Password"
          controlSx={{
            marginTop: i18n.t(k.PX21),
            width: '100%',
            display: i18n.t(k.FLEX)
          }}
          value={pwdStatus.confirmPassword}
          required
          type="password"
          fullWidth
          onChange={handleChange('confirmPassword')}
          errormsg={errors.confirmPassword}
          onBlur={() => {
            if (pwdStatus.confirmPassword.length < 6) {
              setErrors((latestErrors) => {
                return {
                  ...latestErrors,
                  confirmPassword: i18n.t(k.CONFIRM_PASSWORD_MUST_BE_CH)
                }
              })
            }
            if (pwdStatus.confirmPassword !== pwdStatus.password) {
              setErrors((latestErrors) => {
                return {
                  ...latestErrors,
                  confirmPassword: PASSWORDS_DO_NOT_MATCH
                }
              })
            }
          }}
          id="sign-in-page-set-password-view-confirm-password"
        />
        {errors.general ? (
          <Typography
            color={theme.palette.error[300]}
            variant="body4"
            style={{ marginTop: '12px' }}
            as="p"
          >
            {errors.general}
          </Typography>
        ) : null}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <Button
          sx={{ width: '184px' }}
          variant="primary"
          size="large"
          loadable
          handleClick={handleClick}
          id="sign-in-page-set-password-view-confirm"
        >
          {i18n.t(k.CONFIRM1)}
        </Button>
        <Typography variant="body3">
          {i18n.t(k.BACK_TO)}&nbsp;
          <Typography
            variant="Link"
            onClick={handleBackToSignIn}
            color={theme.palette.multicolor[300]}
          >
            {i18n.t(k.SIGN_IN1)}
          </Typography>
        </Typography>
      </div>
    </Box>
  )
}

export default SetPassword
